/* Volatile — PDP: variant selection + AJAX add-to-cart + Added-state UI.
 *
 * State model (post-rewrite — driven by `window.VolatileCart`):
 *   • The "Added ✓" / "Add to cart" state is derived from cart contents,
 *     NOT from local clicks. If the currently-selected variant is in
 *     the cart (ANY quantity), the cart-action shows "Added ✓"; if not,
 *     it shows "Add to cart". This means removing the item in the cart
 *     drawer flips the PDP back to "Add to cart" automatically.
 *   • Variant change re-evaluates the same predicate — no more sticky
 *     "Added" label after switching from a cart variant to a fresh one.
 *   • Add-to-cart is optimistic: clicking the ATC button updates
 *     VolatileCart synchronously (so the pill flips to "Added ✓"
 *     instantly), then the network request reconciles in the
 *     background. Failure rolls the state back via a refresh().
 *
 * No qty stepper here — each click adds 1. To buy more, edit qty in the
 * cart drawer. The Visit-cart button slides in beside "Added ✓" once
 * the variant lands in the cart.
 */
(() => {
  const D = false;
  const LOG = (...a) => { if (D) console.log('[pdp]', ...a); };
  const WARN = (...a) => console.warn('[pdp]', ...a);

  const section = document.querySelector('.v-pdp');
  if (!section) return;

  const form = section.querySelector('[data-pdp-form]');
  const variantInput = section.querySelector('[data-pdp-variant-input]');
  const priceEl = section.querySelector('[data-pdp-price]');
  const atcBtn = section.querySelector('[data-pdp-atc]');
  const atcLabel = section.querySelector('[data-pdp-atc-label]');
  const cartAction = section.querySelector('[data-pdp-cart-action]');
  const dataScript = document.querySelector('[data-pdp-variants]');

  if (!variantInput) { WARN('no variant input'); return; }
  if (!dataScript)   { WARN('no variants JSON'); return; }

  let variants = [];
  try { variants = JSON.parse(dataScript.textContent); }
  catch (err) { WARN('variants JSON parse failed', err); return; }

  LOG('loaded', variants.length, 'variants. initial input.value=', variantInput.value);

  /* ---------- Money formatting (matches snippets/v-price.liquid) ---------- */
  const money = (cents) => {
    const n = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 })
      .format(Math.round(cents / 100));
    return `INR ${n}`;
  };

  /* ---------- Selection helpers ---------- */
  const getSelectedOptions = () => {
    return Array.from(section.querySelectorAll('[data-pdp-option]')).map((group) => {
      const active = group.querySelector('.v-pdp__swatch.is-selected');
      return active ? active.dataset.optionValue : null;
    });
  };
  const updateLabels = () => {
    section.querySelectorAll('[data-pdp-option]').forEach((group) => {
      const label = group.querySelector('[data-pdp-option-label]');
      const active = group && group.querySelector('.v-pdp__swatch.is-selected');
      if (label && active) label.textContent = active.dataset.optionValue;
    });
  };
  const matchVariant = (selected) => {
    if (!variants.length) return null;
    return variants.find((v) =>
      v.options.length === selected.length &&
      v.options.every((opt, i) => opt === selected[i])
    );
  };

  /* ---------- ATC label states ---------- */
  const LABEL_ADD = '<span>Add to cart</span>';
  const LABEL_ADDING = '<span>Adding...</span>';
  const LABEL_ADDED = '<span>Added <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="display: inline-block; vertical-align: -2px; margin-left: 4px;"><polyline points="20 6 9 17 4 12"/></svg></span>';
  const LABEL_SOLD_OUT = '<span>Sold out</span>';

  const setLabel = (html) => { if (atcLabel) atcLabel.innerHTML = html; };
  const setSplit = (split) => {
    if (!cartAction) return;
    cartAction.classList.toggle('is-added', !!split);
  };

  /* ---------- Variant lookup — DOM-as-source-of-truth ----------
     `getCurrentVariant()` reads the form's hidden variant input on
     every call and resolves it against the parsed `variants` array.
     Earlier this lived in a closure-captured `let _currentVariant`
     that was set in `applyVariant`, but a real production trace
     showed the subscriber callback occasionally observing
     `_currentVariant === null` even after `applyVariant` had set it
     — likely a closure-binding race when multiple deferred scripts
     loaded out of order. Reading from the DOM eliminates the race
     entirely: variantInput.value is server-rendered to the selected
     variant, applyVariant updates it on every swatch click, and the
     subscriber doesn't depend on any local state. */
  const getCurrentVariant = () => {
    const id = Number(variantInput && variantInput.value);
    if (!id) return null;
    return variants.find((v) => Number(v.id) === id) || null;
  };

  const renderCartActionState = () => {
    const cv = getCurrentVariant();
    if (!cv) return;
    if (atcBtn) atcBtn.disabled = !cv.available;
    if (!cv.available) {
      setSplit(false);
      setLabel(LABEL_SOLD_OUT);
      return;
    }
    /* Source of truth: VolatileCart. If the current variant has any
       quantity in the cart, render the "Added ✓" + Visit-cart split;
       otherwise render the bare "Add to cart" pill. */
    const inCart = window.VolatileCart
      ? window.VolatileCart.isVariantInCart(cv.id)
      : false;
    setSplit(inCart);
    setLabel(inCart ? LABEL_ADDED : LABEL_ADD);
  };

  const applyVariant = (variant) => {
    if (!variant) {
      if (atcBtn) atcBtn.disabled = true;
      setLabel('<span>Unavailable</span>');
      setSplit(false);
      WARN('no matching variant for current selection');
      return;
    }
    variantInput.value = String(variant.id);
    variantInput.setAttribute('value', String(variant.id));
    if (priceEl) {
      if (!priceEl.querySelector('.v-price')) priceEl.textContent = money(variant.price);
    }

    /* Render state from cart truth — getCurrentVariant() now resolves
       to the freshly-set variant via variantInput.value. */
    renderCartActionState();

    try {
      const url = new URL(window.location);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    } catch (_) {}
    LOG('variant →', variant.id, variant.title, '| available=', variant.available);
  };

  const withCart = (fn) => {
    if (window.VolatileCart) {
      fn(window.VolatileCart);
      return;
    }
    let done = false;
    const run = () => {
      if (done || !window.VolatileCart) return;
      done = true;
      fn(window.VolatileCart);
    };
    window.addEventListener('volatile:cart:ready', run, { once: true });
    document.addEventListener('DOMContentLoaded', run, { once: true });
    setTimeout(run, 0);
    setTimeout(run, 250);
  };

  withCart((cart) => {
    cart.subscribe(() => renderCartActionState());
  });

  // Event delegation on the section so late-added swatches also work.
  section.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pdp-swatch]');
    if (!btn) return;
    const group = btn.closest('[data-pdp-option]');
    if (!group) return;
    e.preventDefault();

    group.querySelectorAll('[data-pdp-swatch]').forEach((other) => {
      const match = other === btn;
      other.classList.toggle('is-selected', match);
      other.setAttribute('aria-pressed', match ? 'true' : 'false');
    });

    updateLabels();
    const selected = getSelectedOptions();
    LOG('click', btn.dataset.optionValue, '→ selected=', selected);
    applyVariant(matchVariant(selected));
  });

  // Initialise
  updateLabels();
  applyVariant(matchVariant(getSelectedOptions()));

  /* ---------- Header count helper (for environments without
        VolatileCart, e.g. very early script execution). ---------- */
  const setHeaderCount = (count) => {
    document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  };
  withCart((cart) => {
    cart.subscribe((state) => setHeaderCount(state.item_count));
  });

  let opQueue = Promise.resolve();
  const enqueue = (job) => {
    opQueue = opQueue.then(job).catch((err) => WARN('cart op failed', err));
    return opQueue;
  };

  let addPending = false;
  const addOne = async () => {
    const variantId = Number(variantInput.value);
    if (!variantId) return;
    const formData = new FormData();
    formData.set('id', String(variantId));
    formData.set('quantity', '1');
    addPending = true;
    if (atcBtn) atcBtn.disabled = true;
    setSplit(false);
    setLabel(LABEL_ADDING);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) throw new Error(`add.js HTTP ${res.status}`);
      await res.json();
      if (window.VolatileCart) await window.VolatileCart.refresh();
      if (window.VolatileUI && typeof window.VolatileUI.refreshCartDrawer === 'function') {
        await window.VolatileUI.refreshCartDrawer();
      }
      renderCartActionState();
    } catch (err) {
      WARN('add failed', err);
      if (window.VolatileCart) await window.VolatileCart.refresh();
      renderCartActionState();
    } finally {
      addPending = false;
      const cv = getCurrentVariant();
      if (atcBtn) atcBtn.disabled = !cv || !cv.available;
    }
  };

  /* ---------- Form submit (the ATC button) ---------- */
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (addPending) return;
      if (atcBtn && atcBtn.disabled) { WARN('ATC disabled, ignoring submit'); return; }

      const cv = getCurrentVariant();

      // Re-clicks on the post-add "Added ✓" are no-ops. The Visit cart
      // pill to the right is the way to view the cart from this state.
      if (cv && window.VolatileCart && window.VolatileCart.isVariantInCart(cv.id)) return;

      enqueue(addOne);
    });
  }
})();
